import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import type { ChartHistoryFilterType } from "@roman/shared/constants/predictionChart";
import type { Choice } from "@roman/shared/features/chart/types";
import { createChartConfig } from "@roman/shared/features/chart/utils";
import { useGetPredictionMarketDetails } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketDetails";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Chart } from "./Chart";
import { extractChoicesFromDetails } from "./utils/extractChoicesFromDetails";
import { getChartData } from "./utils/getChartData";
import { getDefaultShowChartIds } from "./utils/getDefaultShowChartIds";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * This page is specifically designed for use within a React Native WebView.
 * It handles communication with the React Native app through the postMessage API.
 */
export const PredictionMarketChartForReactNativeWebView = () => {
  const { predictionMarketId } = useParams<{ predictionMarketId: string }>();
  const defaultShowChartIds = getDefaultShowChartIds();

  const [isLineVisible, setIsLineVisible] = useState(false);
  const [displayMode, setDisplayMode] = useState<ChartHistoryFilterType>("all");

  // データの取得
  const { data: predictionMarketDetails, isLoading } = useGetPredictionMarketDetails({
    predictionMarketId,
  });

  const { choices } = useMemo(() => extractChoicesFromDetails(predictionMarketDetails), [predictionMarketDetails]);

  // React Nativeから受信したチャート設定データ
  const [rnSortedChoices, setRnSortedChoices] = useState<Choice[]>([]);
  const [rnColorsByChoiceId, setRnColorsByChoiceId] = useState<Record<string, string>>({});
  const [rnEntryCloseAt, setRnEntryCloseAt] = useState<number | null>(null);

  const isEntryStillOpen = rnEntryCloseAt ? rnEntryCloseAt > Date.now() : false;
  const marketEndTime = isEntryStillOpen ? null : rnEntryCloseAt;

  const [selectedChoiceIds, setSelectedChoiceIds] = useState<string[]>(defaultShowChartIds ?? []);

  const selectedChoices = rnSortedChoices.filter((choice) => selectedChoiceIds.includes(choice.id));

  const chartData = useMemo(
    () => getChartData(predictionMarketDetails, choices, displayMode, marketEndTime),
    [predictionMarketDetails, choices, displayMode, marketEndTime],
  );

  const chartConfig = createChartConfig({
    sortedChoices: rnSortedChoices,
    colorsByChoiceId: rnColorsByChoiceId,
  });

  // ライン表示制御
  // 1フレームずらしてからラインを描画
  useEffect(() => {
    if (!isLoading && chartData.length > 0) {
      window.requestAnimationFrame(() => {
        setIsLineVisible(true);
      });
    }
  }, [isLoading, chartData.length]);

  // モバイル側にチャート準備状態を通知
  useEffect(() => {
    const isReady = !isLoading && chartData.length > 0;
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "chartReady", isReady }));
  }, [isLoading, chartData.length]);

  // React Native WebViewからのメッセージを受信
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "initSortedChoices") {
          setRnSortedChoices(data.sortedChoices);
        }
        if (data.type === "initColorsByChoiceId") {
          setRnColorsByChoiceId(data.colorsByChoiceId);
        }
        if (data.type === "initEntryCloseAt") {
          setRnEntryCloseAt(data.entryCloseAt);
        }
        if (data.type === "changeDisplayMode") {
          // すべて or 1M or 1W or 1D
          setDisplayMode(data.displayMode);
        }
        if (data.type === "changeSelectedChoiceIds") {
          // 選択肢の変更
          setSelectedChoiceIds(data.selectedChoiceIds);
        }
      } catch (error) {
        console.error("Failed to parse message:", error);
      }
    };

    window.addEventListener("message", handleMessage);

    // For debugging - expose a global function
    (window as any).receiveMessage = (data: string) => {
      handleMessage({ data } as MessageEvent);
    };

    return () => {
      window.removeEventListener("message", handleMessage);
      (window as any).receiveMessage = undefined;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <LoadingSpinner size="sm" borderStyle="border-gray-400" />
      </div>
    );
  }

  return (
    <div className="bg-white px-5">
      <Chart
        chartData={chartData}
        selectedChoices={selectedChoices}
        selectedChoiceIds={selectedChoiceIds}
        chartConfig={chartConfig}
        displayMode={displayMode}
        isLineVisible={isLineVisible}
      />
    </div>
  );
};
